import { fetch } from 'global';
import { LINK_TYPE_NOWPLAYING, METADATA_TYPE_STREAM, ARTWORK_API_URL, METADATA_TYPE_NOWPLAYING, METADATA_TYPE_METADATA } from './constants';
import Poller from './utils/poller';
import Logger from '../utils/logger';
import EventEmitter from '../utils/event-emitter';
import { createMetadata } from './utils/models';

/**
 * Maintains the "now playing" entries for a player
 */
export default class NowPlaying extends EventEmitter {
  static get defaultOptions () {
    return {
      logger: Logger,
      maxHistory: 20,
      interval: 5000,
      artworkApiUrl: ARTWORK_API_URL,
      fetchArtwork: false
    };
  }

  constructor (options) {
    super();

    options = Object.assign({}, NowPlaying.defaultOptions, options);

    this._history = [];
    this._logger = options.logger;
    this._timeline = options.timeline;
    this._player = options.player;
    this._currentEntry = null;
    this._maxHistory = options.maxHistory;
    this._currentPollEntry = null;
    this._fetchArtworkEnabled = options.fetchArtwork;
    this._artworkApiUrl = options.artworkApiUrl;
    this._poller = new Poller({
      interval: options.interval,
      withCredentials: false
    });

    this._poller.subscribe(this._pollerHandler, this);

    // update the now playing data when current metadata changes
    this._timeline.on('change', this._timelineHandler, { context: this });

    // bind playback events
    this._player.on('loadedmetadata', this.startPolling, { context: this });
    this._player.on('play', this.startPolling, { context: this });
    this._player.on('pause', this.stopPolling, { context: this });
    this._player.on('stop', this.stopPolling, { context: this });
  }

  /**
   * Retrieve the current "now playing" entry
   * @return {object|null}
   */
  getCurrentEntry () {
    return this._currentEntry;
  }

  /**
   * Retrieve a list of entries previously played
   * @return {object[]}
   */
  getHistory () {
    return this._history.slice(0);
  }

  /**
   * Update the current active "now playing" entry
   * @param  {object} entry The new "now playing" entry
   * @return {this}
   */
  setCurrentEntry (entry) {
    if (!this.isNewEntry(entry)) {
      return this;
    }

    // update current entry
    this._currentEntry = entry;

    if (entry === null) {
      this.emit({
        type: 'change',
        metadata: null
      });
      this._logger.debug('NowPlaying: cleared current now playing data');
      return this;
    }

    // add new entry to history
    this._history.push(entry);

    // truncate history to `maxHistory` entries
    if (this._history.length > this._maxHistory) {
      this._history = this._history.slice(-this._maxHistory);
    }

    this.emit({
      type: 'change',
      metadata: entry
    });

    this._logger.debug('NowPlaying: updated current now playing data');

    // if artwork is missing, request it from the artwork api service
    if (this._fetchArtworkEnabled && entry.contentType === METADATA_TYPE_NOWPLAYING && (!entry.icons || entry.icons.length === 0)) {
      this._logger.debug('NowPlaying: requesting artwork from artwork service');
      this._fetchArtwork(entry)
        .then(json => {
          if (json && json.artwork && json.artwork.length > 0) {
            this._logger.debug('NowPlaying: received artwork from artwork service');
            entry.icons = json.artwork;
            this.emit({
              type: 'change',
              metadata: entry
            });
          }
        })
        .catch(err => {
          if (err.message) {
            this._logger.warn(`NowPlaying: failed to retrieve artwork from artwork service - ${err.message}`);
          } else {
            this._logger.warn('NowPlaying: failed to retrieve artwork from artwork service');
          }
        });
    }

    return this;
  }

  /**
   * Compare an entry to the current entry to determine if it's been changed
   * @param  {Object|null}  entry The new entry to be compared
   * @return {Boolean}
   */
  isNewEntry (entry) {
    const currentEntry = this._currentEntry;

    if ((!entry && currentEntry) || (entry && !currentEntry)) {
      return true;
    }

    const newHash = this.createEntryHash(entry);
    const currentHash = this.createEntryHash(currentEntry);

    return newHash !== currentHash;
  }

  /**
   * Creates a hash from an entry, used when comparing entries
   *
   * If the entry is empty the `null` will be returned
   * The format of the hash is `contentType:title:creator|artist:publishDate`
   * If any of the fields used in the hash are missing, that section is removed from the hash
   *
   * @param  {Object|null} entry The entry to generate a hash for
   * @return {string|null}
   */
  createEntryHash (entry) {
    const hash = [];

    if (!entry) {
      return null;
    }

    if (entry.contentType) {
      hash.push(entry.contentType);
    }

    if (entry.title) {
      hash.push(entry.title);
    }

    if (entry.creator || entry.artist) {
      hash.push(entry.creator || entry.artist);
    }

    if (entry.publishDate) {
      hash.push(new Date(entry.publishDate).getTime());
    }

    return hash.join(':');
  }

  /**
   * Start polling for iono metadata
   */
  startPolling () {
    const playlistItem = this._player.playlist.getPlaylistItem();
    if (playlistItem.metadata.contentType === METADATA_TYPE_STREAM) {
      const url = this._getNowPlayingUrl(playlistItem);

      if (url) {
        this._poller.setUrl(url);
        this._poller.start();
      }
    }
  }

  /**
   * Stop polling for iono metadata
   */
  stopPolling () {
    this._poller.stop();
  }

  /**
   * Fetch artwork from the artwork api
   *
   * @param  {Object} entry
   * @return {Promise}
   */
  _fetchArtwork (entry) {
    const deferred = Object.create(null);

    // Build "title" query from metadata
    const artist = entry.artist || entry.creator || '';
    const title = entry.title;

    // Don't make artwork requests when artist AND title are empty
    if (!artist && !title) {
      return Promise.resolve(null);
    }

    const query = `${artist} - ${title}`.toLowerCase();

    const apiUrl = this._artworkApiUrl.replace('<title>', encodeURIComponent(query));
    const options = {
      // no need for cookies to be sent to the api
      credentials: 'omit'
    };

    const promise = new Promise(function (resolve, reject) {
      deferred.resolve = resolve;
      deferred.reject = reject;
    });

    // validate api resposne
    const validateResponse = function (response) {
      if (response.status >= 200 && response.status < 300) {
        return response.json();
      } else {
        const error = new Error(response.statusText);
        error.response = response;
        throw error;
      }
    };

    // parse api response
    const parseJson = function (json) {
      deferred.resolve(json);
    };

    // catch errors
    const catchErrors = function (err) {
      deferred.reject(err);
    };

    // execute api request
    fetch(apiUrl, options)
      .then(validateResponse)
      .then(parseJson)
      .catch(catchErrors);

    return promise;
  }

  /**
   * Get the now playing metadata url from stream links
   * @param {object} stream
   */
  _getNowPlayingUrl (stream) {
    for (let i = 0, len = stream.links.length; i < len; i++) {
      if (stream.links[i].kind === LINK_TYPE_NOWPLAYING) {
        return stream.links[i].url;
      }
    }
  }

  /**
   * Handler for polling responses
   *
   * Circumvents the timeline as our metadata entries don't provide an easy way to determine their position in the users stream
   *
   * @todo handle errrors
   * @param {Error}  err
   * @param {object} response
   */
  _pollerHandler (err, response) {
    if (err) {
      throw err;
    } else {
      response
        .json()
        .then(this._logger.wrap(entries => {
          let responseItem = null;

          // choose the first available entry
          if (Array.isArray(entries)) {
            if (entries.length >= 1) {
              responseItem = createMetadata({ ...entries[0].metadata, contentType: METADATA_TYPE_NOWPLAYING });
            }
          } else {
            responseItem = createMetadata({ ...entries.metadata, contentType: METADATA_TYPE_NOWPLAYING });
          }

          // check if the now playing item in the response differs from the current now playing item
          if (this.isNewEntry(responseItem)) {
            this._currentPollEntry = responseItem;
            this.setCurrentEntry(responseItem);
          }
        }))
        // ignore responses that fail to parse
        .catch(err => {
          this._logger.logException(new Error('Failed to parse nowplaying data: ' + err.message));
        });
    }
  }

  /**
   * Update the now playing data when an entry on the timline becomes active
   * @param {object} event
   */
  _timelineHandler (event) {
    const entry = this._timeline.current();

    // currently now playing data is only supported on live sources
    if (entry) {
      if (entry.metadata && entry.metadata.contentType === METADATA_TYPE_NOWPLAYING) {
        this.setCurrentEntry(entry.metadata);
      } else if (entry.action && entry.action === 'CLEAR') {
        this.setCurrentEntry(null);
      }
    } else {
      this.setCurrentEntry(null);
    }
  }
}
